import Layout from '@/components/Layout'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import {
  Calendar,
  Clock,
  FileText,
  FlaskConical,
  MapPin,
  Pill,
  Stethoscope,
  Video
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { StatusBadgePaciente } from './StatusBadgePaciente'
import { usePacienteLogado } from './usePacienteLogado'

interface AtendimentoApi {
  id: number
  data_atendimento: string
  status_atendimento: string
  medico?: { pessoa?: { nome?: string } }
  tipoAtendimento?: { nome?: string }
}

interface ExameSolicitadoApi {
  id: number
  status_exame: string
  data_solicitacao: string
  exame?: { nome?: string }
  atendimento?: { id: number }
}

interface AgendaApi {
  id: number
  id_paciente: number
  data_agendamento: string
  hora_inicio: string
  status_agendamento: string
  meet_link?: string | null
  tipoAtendimento?: { nome?: string }
  medico?: { pessoa?: { nome?: string } }
}

const timelineIcons = {
  ATENDIMENTO: Stethoscope,
  DOCUMENTO: FileText,
  EXAME: FlaskConical,
  PRESCRIÇÃO: Pill
}
const timelineColors: Record<string, string> = {
  ATENDIMENTO: 'bg-primary text-primary-foreground',
  DOCUMENTO: 'bg-blue-500 text-white',
  EXAME: 'bg-amber-500 text-white',
  PRESCRIÇÃO: 'bg-green-500 text-white'
}

export default function DashboardPaciente() {
  const {
    paciente,
    loading: loadingPaciente,
    nome,
    idPaciente
  } = usePacienteLogado()
  const [atendimentos, setAtendimentos] = useState<AtendimentoApi[]>([])
  const [agendamentos, setAgendamentos] = useState<AgendaApi[]>([])
  const [exames, setExames] = useState<ExameSolicitadoApi[]>([])
  const [loadingAtendimentos, setLoadingAtendimentos] = useState(true)
  const [loadingAgendamentos, setLoadingAgendamentos] = useState(true)
  const [loadingExames, setLoadingExames] = useState(true)

  useEffect(() => {
    if (idPaciente == null) {
      setLoadingAtendimentos(false)
      setLoadingExames(false)
      return
    }
    ;(async () => {
      try {
        setLoadingAtendimentos(true)
        const res = (await api.get(
          `/api/atendimentos?id_paciente=${idPaciente}&limit=50`
        )) as {
          result?: { data?: AtendimentoApi[] }
        }
        const data = res?.result?.data ?? []
        setAtendimentos(Array.isArray(data) ? data : [])
      } catch {
        setAtendimentos([])
      } finally {
        setLoadingAtendimentos(false)
      }
    })()
  }, [idPaciente])

  useEffect(() => {
    if (idPaciente == null) {
      setLoadingAgendamentos(false)
      return
    }
    ;(async () => {
      try {
        setLoadingAgendamentos(true)
        const res = (await api.get(`/api/agenda`)) as {
          result?: AgendaApi[]
        }
        const lista = Array.isArray(res?.result) ? res.result : []
        setAgendamentos(lista.filter(item => item.id_paciente === idPaciente))
      } catch {
        setAgendamentos([])
      } finally {
        setLoadingAgendamentos(false)
      }
    })()
  }, [idPaciente])

  useEffect(() => {
    if (idPaciente == null) {
      setLoadingExames(false)
      return
    }
    ;(async () => {
      try {
        setLoadingExames(true)
        const res = (await api.get(
          `/api/exames_solicitados/paciente/${idPaciente}`
        )) as {
          result?: ExameSolicitadoApi[]
        }
        setExames(Array.isArray(res?.result) ? res.result : [])
      } catch {
        setExames([])
      } finally {
        setLoadingExames(false)
      }
    })()
  }, [idPaciente])

  const concluidos = atendimentos.filter(
    a => a.status_atendimento === 'CONCLUIDO'
  )
  const pendentesExames = exames.filter(e => e.status_exame === 'SOLICITADO')
  const agendamentosValidos = agendamentos
    .filter(a => a.status_agendamento !== 'CANCELADO')
    .map(a => {
      const dataBase = String(a.data_agendamento || '').split('T')[0]
      const horaBase = String(a.hora_inicio || '00:00:00')
      const horaNormalizada = /^\d{2}:\d{2}$/.test(horaBase)
        ? `${horaBase}:00`
        : horaBase
      const dataHora = new Date(`${dataBase}T${horaNormalizada}`)
      return { agenda: a, dataHora }
    })
    .filter(item => !Number.isNaN(item.dataHora.getTime()))
    .sort((a, b) => a.dataHora.getTime() - b.dataHora.getTime())

  const agora = new Date()
  const proximoAgendamento =
    agendamentosValidos.find(
      item => item.dataHora.getTime() >= agora.getTime()
    ) ?? agendamentosValidos[0]

  const dataProximo = proximoAgendamento?.dataHora
    ? proximoAgendamento.dataHora.toLocaleDateString('pt-BR')
    : '--/--'
  const horaProximo = proximoAgendamento?.dataHora
    ? proximoAgendamento.dataHora.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    : '--:--'

  // Linha do tempo = apenas atendimentos realizados (CONCLUIDO), do mais recente ao mais antigo
  const timeline = [...concluidos]
    .sort(
      (a, b) =>
        new Date(b.data_atendimento).getTime() -
        new Date(a.data_atendimento).getTime()
    )
    .map(a => ({
      id: String(a.id),
      date: new Date(a.data_atendimento).toLocaleDateString('pt-BR'),
      time: new Date(a.data_atendimento).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      type: 'ATENDIMENTO' as const,
      title: a.tipoAtendimento?.nome ?? 'Atendimento',
      subtitle: a.medico?.pessoa?.nome ?? ''
    }))

  const loading =
    loadingPaciente ||
    loadingAtendimentos ||
    loadingExames ||
    loadingAgendamentos

  if (loadingPaciente && !paciente) {
    return (
      <Layout>
        <div className="flex items-center justify-center p-12">
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </Layout>
    )
  }

  if (!paciente) {
    return (
      <Layout>
        <div className="flex items-center justify-center p-12">
          <p className="text-destructive">
            Não foi possível carregar seus dados. Faça login novamente.
          </p>
        </div>
      </Layout>
    )
  }

  const firstName = nome.split(' ')[0] || 'Paciente'

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Olá, {firstName}
          </h1>
          <p className="text-muted-foreground">
            Bem-vindo ao seu portal de saúde
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              icon: Calendar,
              label: 'Consultas Realizadas',
              value: loadingAtendimentos ? '...' : concluidos.length,
              color: 'text-primary',
              bg: 'bg-primary/10'
            },
            {
              icon: FlaskConical,
              label: 'Exames Pendentes',
              value: loadingExames ? '...' : pendentesExames.length,
              color: 'text-amber-600',
              bg: 'bg-amber-500/10'
            },
            {
              icon: FileText,
              label: 'Documentos',
              value: paciente.documentos?.length ?? 0,
              color: 'text-blue-600',
              bg: 'bg-blue-500/10'
            }
          ].map(({ icon: Icon, label, value, color, bg }) => (
            <div
              key={label}
              className="bg-card rounded-xl p-4 shadow-sm border border-border"
            >
              <div
                className={cn(
                  'w-9 h-9 rounded-lg flex items-center justify-center mb-3',
                  bg
                )}
              >
                <Icon className={cn('w-5 h-5', color)} />
              </div>
              <p className="text-2xl font-bold text-foreground">{value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              Próximo Atendimento
            </h3>

            {loadingAgendamentos && agendamentos.length === 0 ? (
              <div className="bg-card rounded-xl shadow-sm border border-border p-8 text-center text-muted-foreground">
                Carregando atendimentos...
              </div>
            ) : proximoAgendamento?.agenda ? (
              <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                <div className="bg-primary text-primary-foreground p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <StatusBadgePaciente
                        status={proximoAgendamento.agenda.status_agendamento}
                      />
                      <h4 className="font-semibold text-lg mt-2">
                        {proximoAgendamento.agenda.tipoAtendimento?.nome ??
                          'Atendimento'}
                      </h4>
                      <p className="text-primary-foreground/80 text-sm">
                        {proximoAgendamento.agenda.medico?.pessoa?.nome ?? '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-primary-foreground/90">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm font-medium">{dataProximo}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">{horaProximo}</span>
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div className="flex items-center gap-3">
                      {proximoAgendamento.agenda.meet_link ? (
                        <Video className="w-5 h-5 text-primary flex-shrink-0" />
                      ) : (
                        <MapPin className="w-5 h-5 text-primary flex-shrink-0" />
                      )}
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          {proximoAgendamento.agenda.tipoAtendimento?.nome ??
                            'Atendimento'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {proximoAgendamento.agenda.meet_link
                            ? 'Atendimento online via Google Meet'
                            : 'Atendimento presencial'}
                        </p>
                      </div>
                    </div>
                    {proximoAgendamento.agenda.meet_link ? (
                      <Button asChild size="sm" className="gap-2">
                        <a
                          href={proximoAgendamento.agenda.meet_link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Video className="w-4 h-4" />
                          Iniciar
                        </a>
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground bg-background px-2 py-1 rounded-md">
                        {horaProximo}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-card rounded-xl shadow-sm border border-border p-8 text-center text-muted-foreground">
                Nenhum atendimento agendado no momento.
              </div>
            )}

            {/* <h3 className="font-semibold text-foreground flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-primary" />
              Acesso Rápido
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {[
                {
                  icon: Calendar,
                  label: "Ver Histórico",
                  to: "/paciente/documentos",
                  color: "text-primary",
                  bg: "bg-primary/10",
                },
                {
                  icon: FlaskConical,
                  label: "Meus Exames",
                  to: "/paciente/exames",
                  color: "text-amber-600",
                  bg: "bg-amber-500/10",
                },
                {
                  icon: FileText,
                  label: "Documentos",
                  to: "/paciente/documentos",
                  color: "text-blue-600",
                  bg: "bg-blue-500/10",
                },
              ].map(({ icon: Icon, label, to, color, bg }) => (
                <Link
                  key={to + label}
                  to={to}
                  className="bg-card border border-border rounded-xl p-4 flex flex-col items-center gap-2 hover:shadow-md hover:border-primary/20 transition-all"
                >
                  <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", bg)}>
                    <Icon className={cn("w-5 h-5", color)} />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground text-center">{label}</span>
                </Link>
              ))}
            </div> */}
          </div>

          {/* <div>
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              Linha do Tempo Clínica
            </h3>
            <p className="text-xs text-muted-foreground mb-2">
              Atendimentos realizados por tipo e médico
            </p>
            <div className="bg-card rounded-xl shadow-sm border border-border p-4">
              {loadingAtendimentos ? (
                <p className="text-sm text-muted-foreground">Carregando atendimentos...</p>
              ) : timeline.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhum atendimento realizado ainda.
                </p>
              ) : (
                <div className="space-y-0">
                  {timeline.map((event, index) => {
                    const Icon = timelineIcons[event.type as keyof typeof timelineIcons];
                    const colorClass = timelineColors[event.type] ?? "bg-muted text-muted-foreground";
                    const isLast = index === timeline.length - 1;
                    return (
                      <div key={event.id} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div
                            className={cn(
                              "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0",
                              colorClass
                            )}
                          >
                            {Icon && <Icon className="w-3.5 h-3.5" />}
                          </div>
                          {!isLast && (
                            <div className="w-px flex-1 bg-border my-1 min-h-[16px]" />
                          )}
                        </div>
                        <div className={cn("pb-4", isLast && "pb-0")}>
                          <p className="text-xs text-muted-foreground mb-0.5">
                            {event.date}
                            {event.time && (
                              <span className="ml-1.5">às {event.time}</span>
                            )}
                          </p>
                          <p className="text-sm font-semibold text-foreground leading-snug">
                            {event.title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {event.subtitle ? `Dr(a). ${event.subtitle}` : "—"}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div> */}
        </div>
      </div>
    </Layout>
  )
}
